import { useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  useNavigate,
  useSearchParams,
  Link as RouterLink,
} from "react-router-dom";
import useApi from "../../hooks/useApi";
import { useSnackbar } from "notistack";
import Form from "../Forms/Form";
import { FormField } from "../Forms/FormField";
import { FieldTypes } from "../Forms/FieldTypes";
import * as Yup from "yup";
import {
  passwordSchema,
  confirmPasswordSchema,
} from "../../common/passwordPolicy";
import { authErrorMessage } from "./authErrorMessage";
import axios from "axios";
import AuthPageFrame, { AUTH_LINK_COLOR } from "./AuthPageFrame";

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const api = useApi(false); // Don't include auth header for password reset
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true);

    try {
      const response = await api.post(
        "/auth/reset-password",
        {
          username: email,
          code: values.code,
          newPassword: values.password,
        },
        {
          // BUG-7's rule: the client instance decides who reports. This screen renders the failure
          // itself, right next to the field the user just filled in, so it opts out of the shared
          // client's app-wide error dialog (NEW-25's request-side twin of the server's body flag).
          //
          // The API sets that same flag on its failure BODIES, which is the contract-level half and
          // covers any other caller. This line covers what a body cannot: the bare, bodiless 429 the
          // rate limiter and the account throttle return, which has nowhere to carry a flag and would
          // otherwise put a modal on top of the snackbar for every throttled attempt.
          suppressErrorDialog: true,
        },
      );

      if (response.data?.success) {
        enqueueSnackbar("Password reset successfully", { variant: "success" });
        navigate("/login");
        return;
      }

      enqueueSnackbar(
        authErrorMessage(
          response.status,
          response.data,
          "Failed to reset password. Please try again.",
        ),
        { variant: "error" },
      );
    } catch (error) {
      // Where every 4xx and 5xx lands, including the bodiless 429 — hence reading the status, not
      // just the message. Before NEW-26 there was only one thing to say here, because the API had
      // only one thing to say.
      const response = axios.isAxiosError(error) ? error.response : undefined;
      enqueueSnackbar(
        authErrorMessage(
          response?.status ?? 0,
          response?.data,
          "Failed to reset password. Please try again.",
        ),
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const fields: FormField[] = [
    {
      id: "code",
      text: "Verification Code",
      fixedLabel: true,
      type: FieldTypes.Text,
      columns: 12,
      yupSchema: Yup.string()
        .required("Code is required")
        .matches(/^\d+$/, "Code must contain only numbers")
        .min(6, "Code must be 6 digits")
        .max(6, "Code must be 6 digits"),
    },
    {
      id: "password",
      text: "New Password",
      fixedLabel: true,
      type: FieldTypes.Text,
      password: true,
      columns: 12,
      yupSchema: passwordSchema(),
    },
    {
      id: "confirmPassword",
      text: "Confirm New Password",
      fixedLabel: true,
      type: FieldTypes.Text,
      password: true,
      columns: 12,
      yupSchema: confirmPasswordSchema("password"),
    },
  ];

  return (
    <AuthPageFrame eyebrow="Account help" title="Choose a new password">
      <Typography
        variant="body1"
        sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}
      >
        Enter the verification code sent to your email and your new password.
      </Typography>

      <Form
        fields={fields}
        onSubmit={handleSubmit}
        submitButtonText={loading ? "Resetting..." : "Reset Password"}
        fieldVariant="outlined"
        centerActionButtons
      />

      <Box sx={{ mt: 3, textAlign: "center" }}>
        <RouterLink to="/forgot-password" style={{ textDecoration: "none" }}>
          <Typography
            variant="body2"
            sx={{
              color: AUTH_LINK_COLOR,
              fontWeight: 500,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Didn't receive a code?
          </Typography>
        </RouterLink>
      </Box>
    </AuthPageFrame>
  );
};

export default ResetPassword;
